import { Ace as AceNamespace } from 'ace-builds';
import { MouseEventHandler, useEffect, useRef, useState } from 'react';

import { AceEditor } from 'modules/aceEditor';
import { AceEditorProps } from 'modules/aceEditor/types';
import { cspTag } from 'modules/secureIframe';
import { Drag } from 'ui/icons';
import { spacing } from 'ui/theme';
import noop from 'utils/noop';
import { removeScript, removeScriptFullDoc } from 'utils/script';

import { sideBySideDefaultId, testIds } from '../constants';
import { useSideBySideEditorContext } from '../contexts/SideBySideEditorContext';
import { useHandleImageModal } from '../hooks/useHandleImageModal';
import { getCurrentElement, getCurrentElementRange, isValidImgElement } from '../utils';
import {
  DragHandle,
  EditorContainer,
  Preview,
  PreviewContainerBodyOnly,
  PreviewContainerFullHtml,
  SideBySideContainer,
} from './Editor.styled';
import { Toolbar } from './toolbar';

export type EditorProps = AceEditorProps & {
  id?: string;
  isFullHtmlDoc?: boolean; // enable support for full HTML
};

export const Editor = ({
  debounceChangePeriod,
  editorProps = {
    enableBasicAutocompletion: true,
    enableLiveAutocompletion: false,
    enableSnippets: true,
    $blockScrolling: Infinity,
  },
  fontSize = 14,
  height = spacing.x48,
  id = sideBySideDefaultId,
  isFullHtmlDoc = false,
  mode = 'handlebars',
  name = 'side-by-side-editor',
  onBlur = noop,
  onChange = noop,
  onFocus = noop,
  onInput = noop,
  onPaste = noop,
  placeholder,
  showPrintMargin = false,
  value = '',
  wrapEnabled = true,
}: EditorProps) => {
  const dragHandle = useRef<HTMLDivElement>(null);
  const toolbar = useRef<HTMLDivElement>(null);
  const editorContainer = useRef<HTMLDivElement>(null);
  const sideBySideContainer = useRef<HTMLDivElement>(null);
  const separatorInitPos = useRef(0);
  const editorWidthOffset = useRef(0);
  const sideBySideContainerWidth = useRef(0);
  const dragHandleWidth = useRef(0);
  const [editorWidth, setEditorWidth] = useState<number>(0);
  const toolbarHeight = toolbar.current?.offsetHeight || 0;
  const handleImageModal = useHandleImageModal();
  const { aceEditorTheme, editor } = useSideBySideEditorContext();

  const onDoubleClick = (e: MouseEvent) => {
    const thisEditor = editor.current;
    if (thisEditor) {
      const currentRange = getCurrentElementRange(thisEditor);
      const element = currentRange ? getCurrentElement(thisEditor, currentRange) : undefined;

      if (isValidImgElement(element)) {
        handleImageModal(currentRange, element as HTMLImageElement);
      }
    }
  };

  const onMouseDown: MouseEventHandler<HTMLElement> = (e) => {
    separatorInitPos.current = e.clientX;
    sideBySideContainerWidth.current = sideBySideContainer.current?.clientWidth || 0;
    editorWidthOffset.current = separatorInitPos.current - editorWidth;
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!separatorInitPos.current) {
      return;
    }
    const intendedWidth = e.clientX - editorWidthOffset.current;
    const maxWidth = Math.round((sideBySideContainerWidth.current - dragHandleWidth.current) * 0.8);
    const minWidth = Math.round((sideBySideContainerWidth.current - dragHandleWidth.current) * 0.2);
    const limitedWidth = Math.min(Math.max(intendedWidth, minWidth), maxWidth);

    setEditorWidth(limitedWidth);
  };

  const onLoad = (editorInstance: AceNamespace.Editor) => {
    editor.current = editorInstance;
    // @ts-ignore
    editorInstance.on('dblclick', onDoubleClick);
  };

  const onMouseUp = () => {
    separatorInitPos.current = 0;
  };

  useEffect(() => {
    // manually set width following initial render
    if (!editorWidth) {
      setEditorWidth(editorContainer.current?.clientWidth || 0);
    }
    dragHandleWidth.current = dragHandle.current?.clientWidth || 0;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return (
    <>
      <SideBySideContainer ref={sideBySideContainer} id={id} data-test={testIds.container}>
        <EditorContainer
          ref={editorContainer}
          style={{
            // setting the style prop directly seems to provide better performance over styled.div.attrs
            // using auto and specifying width separately in case we decide to throttle the mouseMove & smooth out the transition
            flex: editorWidth ? `0 0 auto` : `1 1 auto`,
            // initial width of spacing.base is used to account for the spacing.half padding on the PreviewContainer
            width: editorWidth ? `${editorWidth}px` : `${spacing.base}`,
          }}
        >
          <Toolbar innerRef={toolbar} />
          <AceEditor
            debounceChangePeriod={debounceChangePeriod}
            editorProps={editorProps}
            fontSize={fontSize}
            height={`calc(${height} - ${toolbarHeight}px)`}
            mode={mode}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            onInput={onInput}
            onLoad={onLoad}
            onPaste={onPaste}
            placeholder={placeholder}
            showPrintMargin={showPrintMargin}
            theme={aceEditorTheme}
            value={value}
            width={`${editorWidth}px`} // allows wrap to adjust to change
            wrapEnabled={wrapEnabled}
          />
        </EditorContainer>
        <DragHandle onMouseDown={onMouseDown} ref={dragHandle} data-test={testIds.resizer}>
          <Drag />
        </DragHandle>
        {isFullHtmlDoc ? (
          <PreviewContainerFullHtml height={height}>
            {removeScriptFullDoc(value, cspTag)}
          </PreviewContainerFullHtml>
        ) : (
          <PreviewContainerBodyOnly height={height}>
            <Preview dangerouslySetInnerHTML={{ __html: removeScript(value) }} />
          </PreviewContainerBodyOnly>
        )}
      </SideBySideContainer>
    </>
  );
};
